<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { CompleteRegistrationStepContext, LoginFormProps } from '.';
import messages from './LoginForm.locale.json';
import FirstStep from '@/components/LoginForm/Steps/FirstStep/FirstStep.vue';
import SecondStep from '@/components/LoginForm/Steps/SecondStep/SecondStep.vue';
import ThirdStep from '@/components/LoginForm/Steps/ThirdStep/ThirdStep.vue';
import FourthStep from '@/components/LoginForm/Steps/FourthStep/FourthStep.vue';
import { useEvents } from '~/composables/events/useEvents';
import {
	MixpanelLoginTypeValues,
	MixpanelProps,
	MixpanelUserActionValues
} from '~/core/service/mixpanel';

const { trackSignAndLogin } = useEvents();
defineProps<LoginFormProps>();

interface Emits {
	(e: 'otpLogin' | 'socialLogin', value: string): void;
	(e: 'validationCode', value: { phone: string; otp: string }): void;
	(e: 'socialValidationCode', otp: string): void;
	(
		e: 'completePhoneRegistration',
		value: { email?: string; name: string }
	): void;
	(e: 'completeSocialRegistration', value: string): void;
}

const emit = defineEmits<Emits>();

const { t } = useI18n({
	useScope: 'local',
	messages
});

const isOTPValid = defineModel<boolean | null>('isOTPValid', {
	default: null
});

const currentStep = shallowRef<number>(0);
const title = computed<string>(() => t(`steps.${currentStep.value}.title`));
const description = computed<string>(() =>
	t(`steps.${currentStep.value}.description`)
);

const completeRegistrationStepContext = computed<'sms' | 'social'>(() =>
	method.value !== 'sms' ? 'social' : 'sms'
);

const completeRegistrationStepDescription = computed<string>(() =>
	t(
		`steps.${currentStep.value}.${completeRegistrationStepContext.value}.description`
	)
);

const isFirstStep = computed<boolean>(() => currentStep.value === 0);
const isSecondStep = computed<boolean>(() => currentStep.value === 1);
const isThirdStep = computed<boolean>(() => currentStep.value === 2);
const isFourthStep = computed<boolean>(() => currentStep.value === 3);
const getCurrentStep = computed<number>(() => currentStep.value);
const getMethodValidation = computed<string>(() => methodValidation.value);

function handleChangeNumber() {
	if (!isFourthStep.value) {
		goToSecondStep();
		return;
	}

	hasValidatedPhone.value = false;
}

function goToSecondStep() {
	currentStep.value = 1;
}

function goToThirdStep() {
	currentStep.value = 2;
}

function goToFourthStep() {
	currentStep.value = 3;
}

function goToPreviousStep() {
	if (!isFourthStep.value) {
		currentStep.value--;
		return;
	}

	if (
		completeRegistrationStepContext.value === 'social' &&
		hasValidatedPhone.value
	) {
		hasValidatedPhone.value = false;
		return;
	}

	goToThirdStep();
}

const method = shallowRef<'' | CompleteRegistrationStepContext>('');
const phone = ref<string>('');
const email = ref<string | undefined>('');
const name = ref<string>('');
const methodValidation = ref<string>('');
defineExpose({
	title,
	method,
	goToSecondStep,
	goToPreviousStep,
	goToFourthStep,
	isFirstStep,
	isSecondStep,
	isThirdStep,
	isFourthStep,
	getCurrentStep,
	getMethodValidation
});

const shouldShowSecondStep = computed<boolean>(() => {
	if (isSecondStep.value) return true;

	return (
		isFourthStep.value &&
		completeRegistrationStepContext.value === 'social' &&
		!hasValidatedPhone.value
	);
});

const shouldShowThirdStep = computed<boolean>(() => {
	if (isThirdStep.value) return true;

	if (completeRegistrationStepContext.value !== 'social') return false;

	return isFourthStep.value && hasValidatedPhone.value;
});

whenever(
	() => shouldShowThirdStep.value,
	() => {
		trackSignAndLogin.trackingLoginAuthentication({
			[MixpanelProps.CURRENT_PAGE]: route.fullPath,
			[MixpanelProps.USER_NAME]: user.value?.name || '',
			[MixpanelProps.USER_EMAIL]: user.value?.email || '',
			[MixpanelProps.USER_PHONE]: user.value?.phoneNumber || '',
			[MixpanelProps.USER_ACTION]: MixpanelUserActionValues.SIGN_UP,
			[MixpanelProps.LOGIN_TYPE]: MixpanelLoginTypeValues.PHONE
		});
	}
);

const shouldShowFourthStep = computed<boolean>(
	() => isFourthStep.value && completeRegistrationStepContext.value === 'sms'
);

async function handleMethodValidation(method: string) {
	methodValidation.value = method;
	if (method !== 'sms') {
		emit('socialLogin', method);
		return;
	}

	if (isFourthStep.value) return;

	goToSecondStep();
}

function handleResend() {
	if (isFourthStep.value) {
		emit('completeSocialRegistration', phone.value);
		return;
	}

	emit('otpLogin', phone.value);
}

function handleOTPValidation(otp: string) {
	if (completeRegistrationStepContext.value === 'social') {
		emit('socialValidationCode', otp);
		return;
	}

	emit('validationCode', { phone: phone.value, otp });
}

function handleSMSCompleteRegistration(params: {
	email?: string;
	name: string;
}) {
	email.value = params.email;
	name.value = params.name;

	emit('completePhoneRegistration', { email: email.value, name: name.value });
}

const hasValidatedPhone = shallowRef<boolean>(false);

function handlePhoneValidation(newPhone: string) {
	phone.value = newPhone;
	hasValidatedPhone.value = true;

	if (isFourthStep.value) {
		emit('completeSocialRegistration', newPhone);
		return;
	}

	goToThirdStep();
	emit('otpLogin', newPhone);
}

const route = useRoute();
const { user } = useUserSession();
onMounted(() => {
	trackSignAndLogin.trackLoginOpened({
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.USER_NAME]: user.value?.name || '',
		[MixpanelProps.USER_EMAIL]: user.value?.email || '',
		[MixpanelProps.USER_PHONE]: user.value?.phoneNumber || ''
	});
});

onUnmounted(() => {
	currentStep.value = 0;
});
</script>

<template>
	<div class="pb-8 text-primary">
		<p class="pb-6">
			{{ isFourthStep ? completeRegistrationStepDescription : description }}
		</p>

		<FirstStep
			v-show="isFirstStep"
			v-model="method"
			@validated="handleMethodValidation"
		/>

		<SecondStep
			v-show="shouldShowSecondStep"
			:is-active="shouldShowSecondStep"
			:method="method"
			@validated="handlePhoneValidation"
		/>

		<ThirdStep
			v-show="shouldShowThirdStep"
			v-model:is-o-t-p-valid="isOTPValid"
			:phone="phone"
			:is-active="shouldShowThirdStep"
			:is-submitting="isLoading"
			@validated="handleOTPValidation"
			@resend="handleResend"
			@change-number="handleChangeNumber"
		/>

		<FourthStep
			v-show="shouldShowFourthStep"
			v-model:email="email"
			v-model:name="name"
			:is-active="shouldShowFourthStep"
			:is-loading="isLoading"
			:context="completeRegistrationStepContext"
			@validated="handleSMSCompleteRegistration"
		/>
	</div>
</template>
